import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaChevronLeft,
  FaChevronRight,
  FaStar,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
} from "react-icons/fa";
import axios from "../../api/axios";
import { useAuth } from "../../contexts/AuthContext";
import BottomNav from "../../components/BottomNav";

interface Doctor {
  id: number;
  name: string;
  avatar_url: string;
  rating: string;
  specialities: { code: string; name: string }[];
  organizations: { code: string; name: string }[];
  departemens?: {
    practitioner_role_id: number;
    departemen_name: string;
    departemen_id: string;
  }[];
}

interface ScheduleAvailable {
  date: string;
  day: string;
}

interface LocationState {
  doctor: Doctor;
  polyCode?: string;
  polyName?: string;
  sickLeaveDate?: string;
}

interface DoctorState {
  doctor: Doctor;
  selectedDate: string;
  selectedTime: string;
  timezone: string;
  polyCode?: string;
  polyName?: string;
}

const DAYS_SHORT = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];
const MONTHS_ID = [
  "Januari",
  "Februari",
  "Maret",
  "April",
  "Mei",
  "Juni",
  "Juli",
  "Agustus",
  "September",
  "Oktober",
  "November",
  "Desember",
];

const BookingSchedule: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { referenceId } = useParams<{ referenceId: string }>();
  const { doctor, polyCode, polyName } = (location.state || {}) as LocationState;
  const { token: authToken } = useAuth();

  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [availableDates, setAvailableDates] = useState<Set<string>>(new Set());
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);

  // Time slot states
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const [timezone, setTimezone] = useState<string>("");
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [isLoadingTime, setIsLoadingTime] = useState(false);
  const [isHoliday, setIsHoliday] = useState<boolean>(false);
  const [dayOfWeek, setDayOfWeek] = useState<number | null>(null);

  // Helper to get the correct Role ID based on selected poly
  const getRoleId = () => {
    if (!doctor) return null;
    if (polyCode && doctor.departemens) {
      const dept = doctor.departemens.find((d) => d.departemen_id === polyCode);
      if (dept) return dept.practitioner_role_id;
    }
    return doctor.departemens?.[0]?.practitioner_role_id || doctor.id;
  };

  // Fetch available dates for a month
  const fetchMonthSchedule = async (year: number, month: number) => {
    setIsLoadingSchedule(true);
    setAvailableDates(new Set());
    setSelectedDate(null);
    setTimeSlots([]);
    setSelectedTime(null);
    try {
      const firstDay = `${year}-${String(month + 1).padStart(2, "0")}-01`;
      const roleId = getRoleId();
      const res = await axios.get(
        `primaya/doctors/${roleId}/schedule/month`,
        {
          params: {
            date: firstDay,
            sick_leave_date: location.state?.sickLeaveDate,
          },
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );
      const schedules: ScheduleAvailable[] = res.data?.schedule_available || [];
      setAvailableDates(new Set(schedules.map((s) => s.date)));
    } catch (err) {
      console.error("Error fetching month schedule:", err);
    } finally {
      setIsLoadingSchedule(false);
    }
  };

  // Fetch time slots for a selected date
  const fetchTimeSlots = async (date: string) => {
    setIsLoadingTime(true);
    setTimeSlots([]);
    setSelectedTime(null);
    setIsHoliday(false);
    setDayOfWeek(null);
    try {
      const roleId = getRoleId();
      const res = await axios.get(`primaya/doctors/${roleId}/schedule`, {
        params: { date, sick_leave_date: location.state?.sickLeaveDate },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setTimeSlots(res.data?.time || []);
      setTimezone(res.data?.timezone || "");
      setIsHoliday(res.data?.is_holiday ?? false);
      setDayOfWeek(res.data?.day_of_week ?? null);
    } catch (err) {
      console.error("Error fetching time slots:", err);
    } finally {
      setIsLoadingTime(false);
    }
  };

  useEffect(() => {
    if (doctor?.id) {
      fetchMonthSchedule(currentYear, currentMonth);
    }
  }, [currentYear, currentMonth, doctor?.id]);

  const handleDateSelect = (dateStr: string) => {
    setSelectedDate(dateStr);
    setSelectedTime(null);
    fetchTimeSlots(dateStr);
  };

  const goToPrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else setCurrentMonth((m) => m - 1);
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else setCurrentMonth((m) => m + 1);
  };

  const buildCalendarDays = () => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const cells: (number | null)[] = [];
    for (let i = 0; i < firstDayOfMonth; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  };

  const calendarDays = buildCalendarDays();

  const formatDateStr = (day: number) =>
    `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  const isPast = (day: number) => {
    const d = new Date(currentYear, currentMonth, day);
    const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return d < t;
  };

  const speciality = doctor.specialities?.[0]?.name || "Dokter";
  const hospital = doctor.organizations?.[0]?.name || "";
  const canContinue = !!selectedDate && !!selectedTime;

  const handleContinue = () => {
    if (!canContinue) return;
    navigate(`/reference/${referenceId}/booking/confirm`, {
      state: { doctor, selectedDate, selectedTime, timezone, polyCode, polyName },
    });
  };

  if (!doctor) {
    navigate(-1);
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-teal-600 text-white px-4 pt-4 pb-5 shadow-md">
        <div className="flex items-center mb-1">
          <button
            onClick={() => navigate(-1)}
            className="mr-3 p-1 rounded-full hover:bg-teal-500 transition"
          >
            <FaArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-lg font-bold">Pilih Jadwal</h1>
            {polyName && <p className="text-teal-100 text-xs">{polyName}</p>}
          </div>
        </div>
      </div>

      <div className="flex-1 pb-36 overflow-y-auto">
        {/* Doctor Preview Card */}
        <div className="mx-4 mt-4 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex items-center gap-3 p-4">
            <img
              src={doctor.avatar_url}
              alt={doctor.name}
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(doctor.name)}&background=0d9488&color=fff&size=64`;
              }}
              className="w-14 h-14 rounded-xl object-cover bg-teal-50 shrink-0"
            />
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-gray-800 text-sm leading-tight">
                {doctor.name}
              </h2>
              <span className="inline-block mt-1 text-xs bg-teal-50 text-teal-700 font-medium px-2 py-0.5 rounded-full">
                {speciality}
              </span>
              <div className="flex items-center gap-2 mt-1">
                <FaStar className="text-yellow-400" size={11} />
                <span className="text-xs text-gray-500">{doctor.rating}</span>
                {hospital && (
                  <span className="text-xs text-gray-400 truncate">
                    • {hospital}
                  </span>
                )}
              </div>
            </div>
          </div>
          {polyName && (
            <div className="bg-teal-50 px-4 py-2 flex items-center gap-2">
              <FaCalendarAlt className="text-teal-500" size={12} />
              <span className="text-xs text-teal-700 font-medium">
                {polyName}
              </span>
            </div>
          )}
        </div>

        {/* ── STEP 1: Calendar ── */}
        <div className="mx-4 mt-4 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Step label */}
          <div className="px-4 pt-3 pb-0">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-teal-500 text-white text-xs flex items-center justify-center font-bold shrink-0">
                1
              </span>
              <p className="text-sm font-semibold text-gray-700">
                Pilih Tanggal
              </p>
            </div>
          </div>

          {/* Month Nav */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 mt-2">
            <button
              onClick={goToPrevMonth}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition text-gray-600"
            >
              <FaChevronLeft size={13} />
            </button>
            <div className="text-center">
              <p className="font-semibold text-gray-800 text-sm">
                {MONTHS_ID[currentMonth]} {currentYear}
              </p>
              {isLoadingSchedule && (
                <p className="text-xs text-teal-500 mt-0.5 animate-pulse">
                  Memuat jadwal...
                </p>
              )}
            </div>
            <button
              onClick={goToNextMonth}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition text-gray-600"
            >
              <FaChevronRight size={13} />
            </button>
          </div>

          {/* Days header */}
          <div className="grid grid-cols-7 px-3 pt-3 pb-1">
            {DAYS_SHORT.map((d) => (
              <div
                key={d}
                className="text-center text-xs font-semibold text-gray-400 py-1"
              >
                {d}
              </div>
            ))}
          </div>

          {/* Calendar cells */}
          <div className="grid grid-cols-7 px-3 pb-4 gap-y-1">
            {calendarDays.map((day, idx) => {
              if (day === null) return <div key={`empty-${idx}`} />;

              const dateStr = formatDateStr(day);
              const isAvailable = availableDates.has(dateStr);
              const isSelected = selectedDate === dateStr;
              const past = isPast(day);
              const disabled = past || !isAvailable || isLoadingSchedule;

              let cellClass =
                "relative mx-auto flex flex-col items-center justify-center w-9 h-9 rounded-xl text-sm font-medium transition-all ";

              if (isSelected) {
                cellClass += "bg-teal-500 text-white shadow-md scale-105";
              } else if (disabled) {
                cellClass += "text-gray-300 cursor-not-allowed";
              } else {
                cellClass +=
                  "bg-teal-50 text-teal-700 cursor-pointer hover:bg-teal-100 border border-teal-200";
              }

              return (
                <div key={dateStr} className="flex justify-center py-0.5">
                  <button
                    disabled={disabled}
                    onClick={() => !disabled && handleDateSelect(dateStr)}
                    className={cellClass}
                  >
                    {day}
                    {isAvailable && !disabled && !isSelected && (
                      <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-teal-400" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 px-4 pb-4 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-teal-50 border border-teal-200" />
              <span>Tersedia</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-teal-500" />
              <span>Dipilih</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-gray-100" />
              <span>Tidak tersedia</span>
            </div>
          </div>
        </div>

        {/* ── STEP 2: Time Slots ── */}
        <div
          className={`mx-4 mt-4 bg-white rounded-2xl shadow-sm border overflow-hidden transition-all duration-300 ${
            selectedDate
              ? "border-gray-100 opacity-100"
              : "border-gray-100 opacity-50 pointer-events-none"
          }`}
        >
          <div className="px-4 pt-3 pb-3 flex items-center justify-between border-b border-gray-100">
            <div className="flex items-center gap-2">
              <span
                className={`w-5 h-5 rounded-full text-white text-xs flex items-center justify-center font-bold shrink-0 transition-colors ${
                  selectedDate ? "bg-teal-500" : "bg-gray-300"
                }`}
              >
                2
              </span>
              <div className="flex flex-col gap-1.5 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-700">
                    Pilih Jam
                  </p>
                </div>
              </div>
            </div>
            {timezone && selectedDate && (
              <span className="text-xs bg-gray-100 text-gray-500 font-medium px-2 py-0.5 rounded-full">
                {timezone}
              </span>
            )}
          </div>

          <div className="p-4">
            {!selectedDate ? (
              <p className="text-sm text-gray-300 text-center py-4">
                Pilih tanggal dahulu untuk melihat jam tersedia
              </p>
            ) : isLoadingTime ? (
              <div className="flex flex-col items-center justify-center py-6 gap-2">
                <div className="w-6 h-6 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
                <p className="text-xs text-gray-400 animate-pulse">
                  Memuat jam tersedia...
                </p>
              </div>
            ) : timeSlots.length === 0 ? (
              <div className="text-center py-6">
                <FaClock size={28} className="mx-auto text-gray-200 mb-2" />
                <p className="text-sm text-gray-400">Tidak ada jam tersedia</p>
                <p className="text-xs text-gray-300 mt-1">
                  Silakan pilih tanggal lain
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {timeSlots.map((time) => {
                  const isActive = selectedTime === time;
                  return (
                    <button
                      key={time}
                      onClick={() => setSelectedTime(time)}
                      className={`
                        py-2.5 rounded-xl text-sm font-semibold transition-all border
                        ${
                          isActive
                            ? "bg-teal-500 text-white border-teal-500 shadow-md shadow-teal-100 scale-105"
                            : "bg-white text-gray-700 border-gray-200 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700"
                        }
                      `}
                    >
                      {time}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Summary banner */}
        {canContinue && (
          <div className="mx-4 mt-3 bg-teal-50 border border-teal-200 rounded-xl p-3 flex items-center gap-3">
            <FaCheckCircle className="text-teal-500 shrink-0" size={18} />
            <div>
              <p className="text-xs text-gray-500">Jadwal dipilih</p>
              <p className="text-sm font-semibold text-teal-700">
                {new Date(selectedDate! + "T00:00:00").toLocaleDateString(
                  "id-ID",
                  {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  },
                )}
              </p>
              <p className="text-xs text-teal-600 mt-0.5">
                Pukul {selectedTime} {timezone}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      <div className="fixed bottom-16 left-0 right-0 px-4 pb-3 bg-gradient-to-t from-gray-50 via-gray-50 to-transparent pt-4">
        <button
          disabled={!canContinue}
          onClick={handleContinue}
          className={`w-full py-3.5 rounded-2xl font-semibold text-sm transition-all ${
            canContinue
              ? "bg-teal-500 hover:bg-teal-600 text-white "
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
          }`}
        >
          {!selectedDate
            ? "Pilih Tanggal Terlebih Dahulu"
            : !selectedTime
              ? "Pilih Jam Terlebih Dahulu"
              : "Lanjutkan Booking"}
        </button>
      </div>

      <BottomNav />
    </div>
  );
};

export default BookingSchedule;
